import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { FaUtensils, FaSignOutAlt, FaClock, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaSpinner, FaSearch, FaTimes, FaHamburger, FaIceCream, FaChevronDown, FaChevronUp, FaExclamationTriangle, FaFire, FaClock as FaClockIcon } from 'react-icons/fa';
import ViewMenuServer from '../server/ViewMenuServer';
import './cookdashboard.css';

const CookInterface = () => {
  const [acceptedItems, setAcceptedItems] = useState([]);
  const [user, setUser] = useState(null);
  const [serverFirstName, setServerFirstName] = useState('');
  const [schedule, setSchedule] = useState(null);
  const navigate = useNavigate();
  const [totalHours, setTotalHours] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDishes, setLoadingDishes] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const menuId = "ZifkqWN99oRgRNM0Nisz";

  // Fonction pour basculer l'expansion d'une carte
  const toggleCardExpansion = (itemId) => {
    setExpandedCards(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Fonction pour obtenir l'icône de catégorie
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'entrees': return FaUtensils;
      case 'plats': return FaHamburger;
      case 'desserts': return FaIceCream;
      default: return FaUtensils;
    }
  };

  // Fonction pour obtenir le nom de catégorie
  const getCategoryName = (category) => {
    switch (category) {
      case 'entrees': return 'Entrées';
      case 'plats': return 'Plats';
      case 'desserts': return 'Desserts';
      default: return category;
    }
  };

  useEffect(() => {
    const fetchUserRole = async (currentUser) => {
      try {
        setLoading(true);
        const userRef = doc(db, 'identifications', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.role !== 'cook') {
            setIsAuthorized(false);
            setTimeout(() => navigate('/login'), 3000);
          } else {
            setServerFirstName(userData.firstName);
          }
        } else {
          setIsAuthorized(false);
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (error) {
        setIsAuthorized(false);
        setTimeout(() => navigate('/login'), 3000);
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchUserRole(currentUser);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (user) {
        try {
          const scheduleRef = collection(db, 'horaires');
          const q = query(scheduleRef, where('employeeId', '==', user.uid));
          const querySnapshot = await getDocs(q);

          querySnapshot.forEach((doc) => {
            setSchedule(doc.data().schedule);
          });
        } catch (error) {
          // Erreur silencieuse
        }
      }
    };

    fetchSchedule();
  }, [user]);

  useEffect(() => {
    const fetchCommandItems = async () => {
      try {
        setLoadingDishes(true);
        const clientsCollection = collection(db, 'clients');
        const unsubscribers = [];

        const unsubscribe = onSnapshot(clientsCollection, (clientsSnapshot) => {
          let allDishes = [];
          let processedClients = 0;
          const totalClients = clientsSnapshot.size;

          if (totalClients === 0) {
            setAcceptedItems([]);
            setFilteredDishes([]);
            setLoadingDishes(false);
            return;
          }

          clientsSnapshot.forEach((clientDoc) => {
            const clientId = clientDoc.id;
            const commandesCollection = collection(db, `clients/${clientId}/commande`);
            const qAccepted = query(commandesCollection, where('statut', '==', 'Accepté'));

            const commandeUnsubscribe = onSnapshot(qAccepted, (commandesSnapshot) => {
              let clientDishes = [];
              
              commandesSnapshot.forEach((commandeDoc) => {
                const commandeData = commandeDoc.data();
                if (commandeData.items && Array.isArray(commandeData.items)) {
                  const commandeItems = commandeData.items
                    .filter(item => item.categorie !== 'boissons')
                    .map((item) => ({
                      ...item,
                      clientId,
                      commandeId: commandeDoc.id,
                      table: commandeData.table,
                      prenom: commandeData.prenom,
                      email: commandeData.email,
                    }));
                  clientDishes = [...clientDishes, ...commandeItems];
                }
              });

              allDishes = allDishes.filter(dish => dish.clientId !== clientId);
              allDishes = [...allDishes, ...clientDishes];

              setAcceptedItems([...allDishes]);
              setFilteredDishes([...allDishes]);
              
              processedClients++;
              if (processedClients === 1) {
                setLoadingDishes(false);
              }
            });

            unsubscribers.push(commandeUnsubscribe);
          });
        });

        unsubscribers.push(unsubscribe);

        return () => {
          unsubscribers.forEach(unsub => unsub());
        };
      } catch (error) {
        setLoadingDishes(false);
      }
    };

    fetchCommandItems();
  }, []);

  const calculateTotalHours = useCallback(async () => {
    if (!schedule || !user) return;
  
    const currentDate = new Date();
    const currentDay = currentDate.getDate(); // Jour actuel du mois
  
    const daysInWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const hoursWorked = [];
  
    // On va vérifier chaque jour de la semaine jusqu'au jour actuel du mois
    for (let i = 0; i < currentDay; i++) {
      const dayOfWeek = daysInWeek[i % 7]; // Calculer le jour de la semaine pour la date
      const daySchedule = schedule[dayOfWeek];
  
      if (daySchedule && daySchedule !== "Ne travaille pas") {
        const periods = daySchedule.split(" / "); // Diviser les périodes par "/"
        let dailyHours = 0;
      
        periods.forEach((period) => {
          const [start, end] = period.split(" - ");
          const startTime = new Date(`2023-12-01T${start}:00`);
          const endTime = new Date(`2023-12-01T${end}:00`);
          const hours = (endTime - startTime) / 1000 / 60 / 60; // Conversion en heures
          dailyHours += hours;
        });
      
        hoursWorked.push(dailyHours); // Ajouter les heures du jour au tableau
      }      
    }
  
    let total = hoursWorked.reduce((acc, hours) => acc + hours, 0); // Calcul du total
  
    // Ajouter les heures supplémentaires depuis la base de données
    try {
      const scheduleRef = collection(db, 'horaires');
      const q = query(scheduleRef, where('employeeId', '==', user.uid));
      const querySnapshot = await getDocs(q);
  
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.heure_supp) {
          total += parseFloat(data.heure_supp);
        }
  
        updateDoc(doc.ref, { total_hours: total });
      });

      setTotalHours(total);
    } catch (error) {
      // Erreur silencieuse
    }
  }, [schedule, user]);

  useEffect(() => {
    calculateTotalHours(); // Appeler le calcul des heures dès que l'emploi du temps est récupéré
  }, [schedule, calculateTotalHours]);

  const handleDishStatusChange = async (clientId, commandeId, dishName, newStatus) => {
    try {
      const commandeRef = doc(db, `clients/${clientId}/commande`, commandeId);
      const commandeSnap = await getDoc(commandeRef);

      if (commandeSnap.exists()) {
        const commandeData = commandeSnap.data();

        const updatedItems = commandeData.items.map((item) =>
          item.nom === dishName
            ? { ...item, dish_statut: newStatus }
            : item
        );

        await updateDoc(commandeRef, { items: updatedItems });

        setAcceptedItems((prevItems) =>
          prevItems.map((item) =>
            item.clientId === clientId &&
            item.commandeId === commandeId &&
            item.nom === dishName
              ? { ...item, dish_statut: newStatus }
              : item
          )
        );
        setFilteredDishes((prevItems) =>
          prevItems.map((item) =>
            item.clientId === clientId &&
            item.commandeId === commandeId &&
            item.nom === dishName
              ? { ...item, dish_statut: newStatus }
              : item
          )
        );
      }
    } catch (error) {
      // Erreur silencieuse
    }
  };

  // Fonction de recherche
  useEffect(() => {
    if (searchTerm) {
      const filtered = acceptedItems.filter(item =>
        item.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.table.toString().includes(searchTerm) ||
        item.prenom.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDishes(filtered);
    } else {
      setFilteredDishes(acceptedItems);
    }
  }, [searchTerm, acceptedItems]);

  const formatHours = (hours) => {
    const fullHours = Math.floor(hours); // Partie entière des heures
    const minutes = Math.round((hours - fullHours) * 60); // Partie décimale convertie en minutes
    return `${fullHours}h${minutes.toString().padStart(2, '0')}`; // Format h:mm avec zéro-padding
  };

  // Afficher l'emploi du temps sous forme de tableau
  const renderSchedule = () => {
    if (!schedule) return <p>Chargement de l'emploi du temps...</p>;

    const daysInFrench = {
      Sunday: "Dimanche",
      Monday: "Lundi",
      Tuesday: "Mardi",
      Wednesday: "Mercredi",
      Thursday: "Jeudi",
      Friday: "Vendredi",
      Saturday: "Samedi"
    };

    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];  

    return (
      <table className="em-schedule-table">
        <thead>
          <tr>
            {dayNames.map((day) => (
              <th key={day}>{daysInFrench[day]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {dayNames.map((day) => (
              <td key={day}>
                {schedule[day] === 'Ne travaille pas' ? 'Ne travaille pas' : schedule[day]}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  };

  if (!isAuthorized) {
    return (
      <div className="cook-dash-unauthorized-container">
        <div className="cook-dash-unauthorized-popup">
          <FaExclamationTriangle className="cook-dash-unauthorized-icon" />
          <h2>Accès Refusé</h2>
          <p>Vous n'êtes pas autorisé à accéder à cette page.</p>
          <p className="cook-dash-unauthorized-redirect">Redirection en cours...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cook-dash-loading-container">
        <FaSpinner className="cook-dash-loading-spinner" />
        <p className="cook-dash-loading-text">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="cook-dash-container">
      {/* Header */}
      <div className="cook-dash-header">
        <div className="cook-dash-header-content">
          <div className="cook-dash-title-section">
            <FaUtensils className="cook-dash-title-icon" />
            <div className="cook-dash-title-text">
              <h1 className="cook-dash-title">Interface Cuisinier</h1>
              {serverFirstName && (
                <p className="cook-dash-subtitle">Bienvenue, {serverFirstName} !</p>
              )}
            </div>
          </div>
          <button 
            className="cook-dash-logout-btn"
            onClick={() => signOut(auth).then(() => navigate('/login'))}
          >
            <FaSignOutAlt />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="cook-dash-quick-actions">
        <button 
          className="cook-dash-action-btn cook-dash-schedule-btn"
          onClick={() => setShowSchedule(true)}
        >
          <FaClock className="cook-dash-action-icon" />
          <div className="cook-dash-action-text">
            <span className="cook-dash-action-title">Mes Heures</span>
            <span className="cook-dash-action-subtitle">{formatHours(totalHours)}</span>
          </div>
        </button>
        
        <button 
          className="cook-dash-action-btn cook-dash-menu-btn"
          onClick={() => setShowMenu(true)}
        >
          <FaUtensils className="cook-dash-action-icon" />
          <div className="cook-dash-action-text">
            <span className="cook-dash-action-title">Menu</span>
            <span className="cook-dash-action-subtitle">Consulter & Gérer</span>
          </div>
        </button>
      </div>

      {/* Section des plats */}
      <div className="cook-dash-dishes-section">
        <div className="cook-dash-section-header">
          <h2 className="cook-dash-section-title">
            <FaFire className="cook-dash-section-icon" />
            Plats à Préparer
          </h2>
          <span className="cook-dash-dishes-count">
            {filteredDishes.length} plat{filteredDishes.length > 1 ? 's' : ''}
          </span>
        </div>

        {/* Barre de recherche */}
        <div className="cook-dash-search-container">
          <FaSearch className="cook-dash-search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom, table ou client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cook-dash-search-input"
          />
          {searchTerm && (
            <button 
              className="cook-dash-search-clear"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Liste des plats */}
        {loadingDishes ? (
          <div className="cook-dash-dishes-loading">
            <FaSpinner className="cook-dash-dishes-spinner" />
            <p>Chargement des plats...</p>
          </div>
        ) : filteredDishes.length > 0 ? (
          <div className="cook-dash-dishes-grid">
            {filteredDishes.map((item, index) => {
              const itemKey = `${item.commandeId}-${index}`;
              const isExpanded = expandedCards[itemKey];
              const isPrepared = item.dish_statut === 'prepared_dish';
              const isNotReady = item.dish_statut === 'not_ready';
              const CategoryIcon = getCategoryIcon(item.categorie);
              
              return (
                <div 
                  key={itemKey} 
                  className={`cook-dash-dish-card ${isPrepared ? 'cook-dash-dish-prepared' : ''}`}
                >
                  <div className="cook-dash-dish-header">
                    <div className="cook-dash-dish-info">
                      <CategoryIcon className="cook-dash-dish-icon" />
                      <div className="cook-dash-dish-details">
                        <h3 className="cook-dash-dish-name">{item.nom}</h3>
                        <span className="cook-dash-dish-quantity">
                          x{item.quantity}
                        </span>
                      </div>
                    </div>
                    <div className={`cook-dash-dish-status ${isPrepared ? 'cook-dash-status-prepared' : isNotReady ? 'cook-dash-status-not-ready' : 'cook-dash-status-pending'}`}>
                      {isPrepared ? <FaCheckCircle /> : isNotReady ? <FaTimesCircle /> : <FaClockIcon />}
                      <span>{isPrepared ? 'Préparé' : isNotReady ? 'Non prêt' : 'En attente'}</span>
                    </div>
                  </div>

                  <div className="cook-dash-dish-meta">
                    <div className="cook-dash-dish-table">
                      <span className="cook-dash-meta-label">Table</span>
                      <span className="cook-dash-meta-value">#{item.table}</span>
                    </div>
                    <div className="cook-dash-dish-client">
                      <span className="cook-dash-meta-label">Client</span>
                      <span className="cook-dash-meta-value">{item.prenom}</span>
                    </div>
                    <div className="cook-dash-dish-category">
                      <span className="cook-dash-meta-label">Catégorie</span>
                      <span className="cook-dash-meta-value">{getCategoryName(item.categorie)}</span>
                    </div>
                  </div>

                  {isExpanded && item.email && (
                    <div className="cook-dash-dish-expanded">
                      <p className="cook-dash-dish-email">📧 {item.email}</p>
                    </div>
                  )}

                  <div className="cook-dash-dish-actions">
                    <button
                      onClick={() => toggleCardExpansion(itemKey)}
                      className="cook-dash-expand-btn"
                    >
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    
                    {isPrepared ? (
                      <button
                        className="cook-dash-action-prepare cook-dash-action-cancel"
                        onClick={() => handleDishStatusChange(item.clientId, item.commandeId, item.nom, 'not_ready')}
                      >
                        <FaTimesCircle />
                        <span>Pas encore prêt</span>
                      </button>
                    ) : (
                      <button
                        className="cook-dash-action-prepare"
                        onClick={() => handleDishStatusChange(item.clientId, item.commandeId, item.nom, 'prepared_dish')}
                      >
                        <FaCheckCircle />
                        <span>Marquer préparé</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cook-dash-no-dishes">
            <FaUtensils className="cook-dash-no-dishes-icon" />
            <h3>Aucun plat à préparer</h3>
            <p>{searchTerm ? 'Aucun résultat pour votre recherche' : 'Tous les plats sont prêts !'}</p>
          </div>
        )}
      </div>
      {/* Popup Emploi du temps */}
      {showSchedule && (
        <div className="cook-dash-popup-overlay" onClick={() => setShowSchedule(false)}>
          <div className="cook-dash-popup" onClick={(e) => e.stopPropagation()}>
            <div className="cook-dash-popup-header">
              <div className="cook-dash-popup-title-section">
                <FaCalendarAlt className="cook-dash-popup-icon" />
                <h2>Emploi du temps</h2>
              </div>
              <button 
                className="cook-dash-popup-close" 
                onClick={() => setShowSchedule(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="cook-dash-popup-content">
              <div className="cook-dash-schedule-table-wrapper">
                {renderSchedule()}
              </div>

              <div className="cook-dash-hours-summary">
                <FaClock className="cook-dash-hours-icon" />
                <div className="cook-dash-hours-details">
                  <span className="cook-dash-hours-label">Total ce mois</span>
                  <span className="cook-dash-hours-value">{formatHours(totalHours)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup Menu */}
      {showMenu && (
        <div className="cook-dash-popup-overlay" onClick={() => setShowMenu(false)}>
          <div className="cook-dash-popup cook-dash-menu-popup" onClick={(e) => e.stopPropagation()}>
            <div className="cook-dash-popup-header">
              <div className="cook-dash-popup-title-section">
                <FaUtensils className="cook-dash-popup-icon" />
                <h2>Menu du Restaurant</h2>
              </div>
              <button 
                className="cook-dash-popup-close" 
                onClick={() => setShowMenu(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="cook-dash-popup-content cook-dash-menu-content">
              <ViewMenuServer menuId={menuId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookInterface;